import { TFunction } from 'i18next';

import {
  Column,
  ColumnFilterElementTemplateOptions,
  ColumnProps,
} from 'primereact/column';
import { formatDate } from '@/utils/formatDate';
import { Row } from 'primereact/row';
import { InputDateFilter, TableDropdownFilter } from '@/components';
import {
  filterApplyTemplate,
  filterClearTemplate,
  inputTextTemplate,
} from '@/components/Table/Table';
import { ColumnGroup } from 'primereact/columngroup';
import { TableData } from '@/components/Table/types';
import { DocumentsHistoryListInfo } from '../../types';
import ModalDetails from './ModalDetails';
import { DownloadPdfDocument } from './DownloadPDFDocument';

export const getTableColumns = (
  translates: TFunction<'Documents', undefined>
): ColumnProps[] => [
  {
    field: 'feTipoCFETexto',
    header: translates('type'),
    filter: true,
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'feSerieCFE',
    header: translates('series'),
    filter: true,
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'feNroCFE',
    header: translates('number'),
    filter: true,
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'fecha',
    header: translates('date'),
    body: (rowData: DocumentsHistoryListInfo) => formatDate(rowData.fecha),
    filter: true,
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'razonSocial',
    header: translates('client'),
    filter: true,
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'rutCliente',
    header: translates('rut'),
    filter: true,
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'oidLiquidacionWeb',
    header: translates('settlement'),
    filter: true,
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'clasificacionDocumento',
    header: translates('classification'),
    filter: true,
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'contenedor',
    header: translates('container'),
    filter: true,
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'nombreViaje',
    header: translates('voyage'),
    filter: true,
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'armador',
    header: translates('shipOwner'),
    filter: true,
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'moneda',
    header: translates('currency'),
    filter: true,
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'importe',
    header: translates('amount'),
    filter: true,
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'view',
    header: '',
    filter: false,
    showFilterMenu: false,
    sortable: false,
    headerStyle: { width: '3rem' },
    frozen: true,
    body: (rowData: DocumentsHistoryListInfo) => (
      <div className="flex justify-center text-blue-800">
        <ModalDetails rowData={rowData} />
      </div>
    ),
    alignFrozen: 'right',
  },
  {
    field: 'oid',
    header: '',
    filter: false,
    showFilterMenu: false,
    sortable: false,
    frozen: true,
    alignFrozen: 'right',
    headerStyle: { width: '3rem' },
    body: (rowData: DocumentsHistoryListInfo) => (
      <div className="flex justify-center text-blue-800">
        {' '}
        <DownloadPdfDocument oid={rowData.identificador} />
      </div>
    ),
  },
];

export const getHeaderColumnsGroup = (
  translates: TFunction<'documentsHistory', undefined>,
  data: DocumentsHistoryListInfo[] | undefined = []
) => (
  <ColumnGroup>
    <Row>
      <Column
        header={translates('type')}
        field="feTipoCFETexto"
        filter
        showFilterMenu
        sortable
        rowSpan={3}
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={inputTextTemplate}
      />

      <Column
        rowSpan={3}
        header={translates('series')}
        field="feSerieCFE"
        filter
        showFilterMenu
        sortable
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterElement={inputTextTemplate}
        filterClear={filterClearTemplate}
      />

      <Column
        rowSpan={3}
        header={translates('number')}
        field="feNroCFE"
        filter
        showFilterMenu
        sortable
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterElement={inputTextTemplate}
        filterClear={filterClearTemplate}
      />

      <Column
        header={translates('date')}
        field="fecha"
        rowSpan={3}
        sortable
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={(options: ColumnFilterElementTemplateOptions) => (
          <InputDateFilter options={options} displayMenu />
        )}
        dataType="date"
        filterField="fecha"
      />

      <Column
        rowSpan={3}
        header={translates('client')}
        field="razonSocial"
        filter
        showFilterMenu
        sortable
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterElement={inputTextTemplate}
        filterClear={filterClearTemplate}
      />

      <Column
        rowSpan={3}
        header={translates('rut')}
        field="rutCliente"
        filter
        showFilterMenu
        sortable
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterElement={inputTextTemplate}
        filterClear={filterClearTemplate}
      />

      <Column
        rowSpan={3}
        header={translates('settlement')}
        field="oidLiquidacionWeb"
        filter
        showFilterMenu
        sortable
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterElement={inputTextTemplate}
        filterClear={filterClearTemplate}
      />

      <Column
        rowSpan={3}
        header={translates('classification')}
        field="clasificacionDocumento"
        filter
        showFilterMenu
        sortable
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterElement={inputTextTemplate}
        filterClear={filterClearTemplate}
      />

      <Column
        rowSpan={3}
        header={translates('container')}
        field="contenedor"
        filter
        showFilterMenu
        sortable
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterElement={inputTextTemplate}
        filterClear={filterClearTemplate}
      />

      <Column
        rowSpan={3}
        header={translates('voyage')}
        field="nombreViaje"
        filter
        showFilterMenu
        sortable
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterElement={inputTextTemplate}
        filterClear={filterClearTemplate}
      />

      <Column
        header={translates('shipOwner')}
        field="armador"
        rowSpan={3}
        sortable
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={(options: ColumnFilterElementTemplateOptions) => (
          <TableDropdownFilter
            options={options}
            data={data as unknown as TableData}
            fieldName="armador"
          />
        )}
      />

      <Column
        rowSpan={3}
        header={translates('currency')}
        field="moneda"
        filter
        showFilterMenu
        sortable
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterElement={inputTextTemplate}
        filterClear={filterClearTemplate}
      />

      <Column
        header={translates('amount')}
        rowSpan={3}
        field="importe"
        filter
        showFilterMenu
        sortable
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterElement={inputTextTemplate}
        filterClear={filterClearTemplate}
      />
      <Column field="view" style={{ width: 50 }} frozen alignFrozen="right" />
      <Column field="oid" style={{ width: 50 }} frozen alignFrozen="right" />
    </Row>
  </ColumnGroup>
);
